'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface IndustryPackage {
  industry: string;
  icon: string;
  description: string;
  services: string[];
  image: string;
  alt: string;
  caseStudyCount: number;
}

interface IndustryPackagesProps {
  packages: IndustryPackage[];
}

const IndustryPackages = ({ packages }: IndustryPackagesProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedIndustry, setSelectedIndustry] = useState<number>(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-card rounded-lg p-6 shadow-subtle border border-border">
            <div className="h-48 bg-muted/50 rounded animate-pulse mb-4"></div>
            <div className="h-6 bg-muted/50 rounded animate-pulse mb-2"></div>
            <div className="h-4 bg-muted/50 rounded animate-pulse"></div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {packages.map((pkg, index) => (
          <button
            key={index}
            onClick={() => setSelectedIndustry(index)}
            className={`p-4 rounded-lg border-2 transition-all duration-300 ease-brand ${
              selectedIndustry === index
                ? 'border-primary bg-primary/10' :'border-border bg-card hover:border-primary/50'
            }`}
          >
            <Icon
              name={pkg.icon as any}
              size={32}
              className={`mx-auto mb-2 ${
                selectedIndustry === index ? 'text-primary' : 'text-muted-foreground'
              }`}
            />
            <p className={`text-sm font-medium text-center ${
              selectedIndustry === index ? 'text-primary' : 'text-foreground'
            }`}>
              {pkg.industry}
            </p>
          </button>
        ))}
      </div>

      <div className="bg-card rounded-lg shadow-subtle border border-border overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="relative h-64 lg:h-auto">
            <AppImage
              src={packages[selectedIndustry].image}
              alt={packages[selectedIndustry].alt}
              className="w-full h-full object-cover"
            />
          </div>

          <div className="p-6 lg:p-8">
            <div className="flex items-center space-x-3 mb-4">
              <Icon
                name={packages[selectedIndustry].icon as any}
                size={32}
                className="text-primary"
              />
              <h3 className="text-2xl font-display font-bold text-foreground">
                {packages[selectedIndustry].industry}
              </h3>
            </div>

            <p className="text-muted-foreground mb-6">
              {packages[selectedIndustry].description}
            </p>

            <div className="mb-6">
              <h4 className="text-lg font-display font-bold text-foreground mb-3">
                Tailored Services
              </h4>
              <ul className="space-y-2">
                {packages[selectedIndustry].services.map((service, index) => (
                  <li key={index} className="flex items-start space-x-2 text-sm text-foreground">
                    <Icon name="CheckCircleIcon" size={16} className="text-success flex-shrink-0 mt-0.5" />
                    <span>{service}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex items-center justify-between pt-6 border-t border-border">
              <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                <Icon name="DocumentTextIcon" size={16} />
                <span>{packages[selectedIndustry].caseStudyCount} Case Studies Available</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IndustryPackages;